import {
    defineStore
} from 'pinia'
import {
    ref,
    computed
} from 'vue'
import websocketService from '@/services/websocketService'
import {
    useAuthStore
} from './auth'

export const useNotificationsStore = defineStore('notifications', () => {
    // État
    const notifications = ref([])
    const connected = ref(false)
    const reconnecting = ref(false)
    const unreadCount = ref(0)
    const soundEnabled = ref(true)
    const desktopNotificationsEnabled = ref(false)

    // Computed
    const unreadNotifications = computed(() =>
        notifications.value.filter(n => !n.read)
    )

    const recentNotifications = computed(() =>
        notifications.value.slice(0, 10)
    )

    /**
     * Initialiser la connexion WebSocket
     */
    const connect = () => {
        const authStore = useAuthStore()
        const token = authStore.token

        if (!token) {
            console.warn('❌ No token available for WebSocket connection')
            return
        }

        console.log('🔌 Connecting to WebSocket with token...')

        try {
            websocketService.connect(token)
            setupListeners()

            console.log('✅ WebSocket setup complete')
        } catch (error) {
            console.error('❌ Failed to connect WebSocket:', error)
        }
    }

    /**
     * Configurer les écouteurs d'événements
     */
    const setupListeners = () => {
        // Événements de connexion
        websocketService.on('connection:success', () => {
            connected.value = true
            reconnecting.value = false
            console.log('✅ Notifications WebSocket connected')
        })

        websocketService.on('connection:lost', () => {
            connected.value = false
            console.log('❌ Notifications WebSocket disconnected')
        })

        websocketService.on('connection:reconnecting', () => {
            reconnecting.value = true
            console.log('🔄 Reconnecting...')
        })

        websocketService.on('connection:reconnected', () => {
            connected.value = true
            reconnecting.value = false
            console.log('✅ Reconnected!')
        })

        // ===== ÉVÉNEMENTS DE NOTIFICATIONS =====

        // Initialisation des notifications (reçu à la connexion)
        websocketService.on('notifications:init', (data) => {
            console.log('📬 Received notifications init:', data)

            if (data.notifications && Array.isArray(data.notifications)) {
                // Formater et ajouter les notifications initiales
                notifications.value = data.notifications.map(notif => {
                    const eventType = notif.type || notif.event || 'dispatch:created'
                    const msg = notif.message || buildDetailedMessage(eventType, notif.metadata || notif.data || notif) || ''
                    return {
                        id: notif.id || notif._id || `${Date.now()}-${Math.random()}`,
                        event: eventType,
                        title: notif.title || getNotificationTitle(eventType),
                        message: msg,
                        data: notif.metadata || notif.data || {},
                        read: notif.readAt !== null,
                        createdAt: notif.createdAt || notif.timestamp || new Date(),
                        userId: notif.userId,
                        channel: notif.channel,
                        status: notif.status
                    }
                })

                console.log('✅ Notifications formatées:', notifications.value)
            }

            if (typeof data.count === 'number') {
                unreadCount.value = data.count
            } else {
                // Compter manuellement
                unreadCount.value = notifications.value.filter(n => !n.read).length
            }

            console.log('📊 Notifications non lues:', unreadCount.value)

            saveToLocalStorage()
        })

        // Compteur de notifications non lues
        websocketService.on('notifications:count', (data) => {
            console.log('🔢 Received notifications count:', data)
            if (typeof data.count === 'number') {
                unreadCount.value = data.count
            }
        })

        // Nouvelle notification
        websocketService.on('notification:new', (data) => {
            console.log('🔔 Received new notification:', data)

            const notification = data.notification || data
            addNotification(
                notification.type || notification.event || 'system:message',
                notification
            )
        })

        // Notification marquée comme lue
        websocketService.on('notification:read', (data) => {
            console.log('✅ Notification marked as read:', data)

            if (data.notificationId) {
                const notif = notifications.value.find(n => n.id === data.notificationId)
                if (notif && !notif.read) {
                    notif.read = true
                    unreadCount.value = Math.max(0, unreadCount.value - 1)
                    saveToLocalStorage()
                }
            }
        })

        // Toutes les notifications marquées comme lues
        websocketService.on('notifications:all_read', () => {
            console.log('✅ All notifications marked as read')
            notifications.value.forEach(n => {
                n.read = true
            })
            unreadCount.value = 0
            saveToLocalStorage()
        })

        // Écouter toutes les notifications d'événements spécifiques
        websocketService.onNotification(({
            event,
            data
        }) => {
            console.log('📨 Received specific notification event:', event, data)
            addNotification(event, data)
        })
    }

    /**
     * Ajouter une notification
     */
    const addNotification = (event, data) => {
        const eventTitle = data.title || getNotificationTitle(event || data.type || data.event)

        // Construire le message détaillé depuis les données si pas de message direct
        const message = data.message || buildDetailedMessage(event, data) || ''

        const notification = {
            id: data.id || `${Date.now()}-${Math.random()}`,
            event: event || data.type || data.event || 'dispatch:created',
            title: eventTitle,
            message,
            data: data.metadata || data.data || data,
            read: data.read !== undefined ? data.read : false,
            createdAt: data.createdAt || data.timestamp || new Date(),
            userId: data.userId,
            channel: data.channel,
            status: data.status
        }

        notifications.value.unshift(notification)

        if (!notification.read) {
            unreadCount.value++
        }

        if (notifications.value.length > 100) {
            notifications.value = notifications.value.slice(0, 100)
        }

        if (soundEnabled.value && !notification.read) {
            playNotificationSound()
        }

        if (desktopNotificationsEnabled.value && !notification.read) {
            showDesktopNotification(notification)
        }

        saveToLocalStorage()
    }

    /**
     * Construire un message détaillé depuis les données de l'événement
     */
    const buildDetailedMessage = (event, data) => {
        const productName = data.productName || (data.product && data.product.name) || data.name
        const labName = data.laboratoryName || (data.laboratory && data.laboratory.name)
        const qty = data.currentStock !== undefined ? data.currentStock : (data.quantity !== undefined ? data.quantity : data.quantityReal)
        const unit = data.unit || ''

        switch (event) {
            case 'stock:low':
            case 'STOCK_LOW':
                return productName ?
                    `Le produit "${productName}" a un stock bas${qty !== undefined ? ` (${qty} ${unit} restant(s))` : ''}${labName ? ` — ${labName}` : ''}` :
                    null
            case 'stock:critical':
            case 'STOCK_CRITICAL':
                return productName ?
                    `Le produit "${productName}" est en stock critique${qty !== undefined ? ` (${qty} ${unit} restant(s))` : ''}${labName ? ` — ${labName}` : ''}` :
                    null
            case 'stock:out':
            case 'STOCK_OUT':
                return productName ?
                    `Le produit "${productName}" est en rupture de stock${labName ? ` — ${labName}` : ''}` :
                    null
            case 'order:created':
            case 'ORDER_CREATED':
                return data.orderNumber ? `Commande ${data.orderNumber} créée` : null
            case 'order:approved':
            case 'ORDER_APPROVED':
                return data.orderNumber ? `Commande ${data.orderNumber} approuvée` : null
            case 'order:received':
            case 'ORDER_RECEIVED':
                return data.orderNumber ? `Commande ${data.orderNumber} reçue` : null
            case 'transfer:requested':
            case 'TRANSFER_PENDING':
                return data.transferNumber ?
                    `Demande de transfert ${data.transferNumber}${data.fromLaboratory ? ` depuis ${data.fromLaboratory}` : ''}` :
                    null
            case 'transfer:approved':
            case 'TRANSFER_APPROVED':
                return data.transferNumber ? `Transfert ${data.transferNumber} approuvé` : null
            case 'transfer:rejected':
            case 'TRANSFER_REJECTED':
                return data.transferNumber ? `Transfert ${data.transferNumber} refusé` : null
            case 'product:expiring':
            case 'PRODUCT_EXPIRING_SOON':
                return productName ?
                    `Le produit "${productName}" expire bientôt${data.expirationDate ? ` (${new Date(data.expirationDate).toLocaleDateString('fr-FR')})` : ''}` :
                    null
            case 'product:expired':
            case 'PRODUCT_EXPIRED':
                return productName ? `Le produit "${productName}" a expiré` : null
            case 'equipment:maintenance_due':
                return data.equipmentName ? `Maintenance requise pour "${data.equipmentName}"` : null
            case 'equipment:out_of_order':
                return data.equipmentName ? `L'équipement "${data.equipmentName}" est hors service` : null
            default:
                return null
        }
    }

    /**
     * Marquer une notification comme lue
     */
    const markAsRead = (notificationId) => {
        const notification = notifications.value.find(n => n.id === notificationId)
        if (notification && !notification.read) {
            notification.read = true
            unreadCount.value--

            // Informer le serveur
            websocketService.markAsRead(notificationId)

            saveToLocalStorage()
        }
    }

    /**
     * Marquer toutes comme lues
     */
    const markAllAsRead = () => {
        notifications.value.forEach(n => {
            n.read = true
        })
        unreadCount.value = 0

        // Informer le serveur
        websocketService.markAllAsRead()

        saveToLocalStorage()
    }

    /**
     * Supprimer une notification
     */
    const removeNotification = (notificationId) => {
        const index = notifications.value.findIndex(n => n.id === notificationId)
        if (index > -1) {
            const notification = notifications.value[index]
            if (!notification.read) {
                unreadCount.value--
            }
            notifications.value.splice(index, 1)
            saveToLocalStorage()
        }
    }

    /**
     * Supprimer toutes les notifications
     */
    const clearAll = () => {
        notifications.value = []
        unreadCount.value = 0
        saveToLocalStorage()
    }

    /**
     * Obtenir le titre de la notification
     */
    const getNotificationTitle = (event) => {
        const titles = {
            'alert:created': '🚨 Nouvelle alerte',
            'alert:updated': '🔄 Alerte mise à jour',
            'stock:low': '⚠️ Stock bas',
            'stock:critical': '🔴 Stock critique',
            'stock:out': '❌ Rupture de stock',
            'stock:updated': '📦 Stock mis à jour',
            'order:created': '🛒 Nouvelle commande',
            'order:approved': '✅ Commande approuvée',
            'order:received': '📥 Commande reçue',
            'order:status_changed': '🔄 Statut commande changé',
            'transfer:requested': '📤 Demande de transfert',
            'transfer:approved': '✅ Transfert approuvé',
            'transfer:rejected': '❌ Transfert rejeté',
            'transfer:completed': '✅ Transfert complété',
            'equipment:maintenance_due': '🔧 Maintenance due',
            'equipment:out_of_order': '⚠️ Équipement hors service',
            'equipment:status_changed': '🔄 Statut équipement changé',
            'product:expiring': '⏰ Produit expirant',
            'product:expired': '❌ Produit expiré',
            'system:message': '📢 Message système',
            'user:mentioned': '👤 Vous êtes mentionné'
        }
        return titles[event] || '🔔 Notification'
    }

    /**
     * Obtenir la sévérité de la notification
     */
    const getNotificationSeverity = (event) => {
        if (event.includes('critical') || event.includes('out') || event.includes('expired')) {
            return 'error'
        }
        if (event.includes('low') || event.includes('expiring') || event.includes('due')) {
            return 'warn'
        }
        if (event.includes('approved') || event.includes('completed') || event.includes('received')) {
            return 'success'
        }
        return 'info'
    }

    /**
     * Jouer un son de notification
     */
    const playNotificationSound = () => {
        try {
            const audio = new Audio('/notification.mp3')
            audio.volume = 0.3
            audio.play().catch(err => console.log('Cannot play sound:', err))
        } catch (error) {
            console.log('Sound not available')
        }
    }

    /**
     * Afficher une notification desktop
     */
    const showDesktopNotification = (notification) => {
        if (!('Notification' in window)) return

        if (Notification.permission === 'granted') {
            const title = getNotificationTitle(notification.event)
            const body = notification.message || notification.title || 'Nouvelle notification'

            new Notification(title, {
                body,
                icon: '/enval.png',
                badge: '/enval.png',
                tag: notification.id
            })
        }
    }

    /**
     * Demander la permission pour les notifications desktop
     */
    const requestDesktopPermission = async () => {
        if (!('Notification' in window)) {
            console.log('Desktop notifications not supported')
            return false
        }

        if (Notification.permission === 'granted') {
            desktopNotificationsEnabled.value = true
            return true
        }

        if (Notification.permission !== 'denied') {
            const permission = await Notification.requestPermission()
            if (permission === 'granted') {
                desktopNotificationsEnabled.value = true
                return true
            }
        }

        return false
    }

    /**
     * Activer/désactiver le son
     */
    const toggleSound = () => {
        soundEnabled.value = !soundEnabled.value
        localStorage.setItem('notificationSoundEnabled', soundEnabled.value)
    }

    /**
     * Activer/désactiver les notifications desktop
     */
    const toggleDesktopNotifications = async () => {
        if (!desktopNotificationsEnabled.value) {
            const granted = await requestDesktopPermission()
            if (!granted) return
        } else {
            desktopNotificationsEnabled.value = false
        }
        localStorage.setItem('desktopNotificationsEnabled', desktopNotificationsEnabled.value)
    }

    /**
     * Sauvegarder dans localStorage
     */
    const saveToLocalStorage = () => {
        try {
            localStorage.setItem('notifications', JSON.stringify(notifications.value.slice(0, 50)))
            localStorage.setItem('unreadCount', unreadCount.value)
        } catch (error) {
            console.error('Failed to save notifications to localStorage:', error)
        }
    }

    /**
     * Charger depuis localStorage
     */
    const loadFromLocalStorage = () => {
        try {
            const saved = localStorage.getItem('notifications')
            if (saved) {
                notifications.value = JSON.parse(saved)
            }

            const savedCount = localStorage.getItem('unreadCount')
            if (savedCount) {
                unreadCount.value = parseInt(savedCount)
            }

            const savedSound = localStorage.getItem('notificationSoundEnabled')
            if (savedSound !== null) {
                soundEnabled.value = savedSound === 'true'
            }

            const savedDesktop = localStorage.getItem('desktopNotificationsEnabled')
            if (savedDesktop !== null) {
                desktopNotificationsEnabled.value = savedDesktop === 'true'
            }
        } catch (error) {
            console.error('Failed to load notifications from localStorage:', error)
        }
    }

    /**
     * Se déconnecter
     */
    const disconnect = () => {
        websocketService.disconnect()
        connected.value = false
        reconnecting.value = false
    }

    /**
     * Réinitialiser
     */
    const reset = () => {
        disconnect()
        notifications.value = []
        unreadCount.value = 0
        localStorage.removeItem('notifications')
        localStorage.removeItem('unreadCount')
    }

    // Charger les notifications au démarrage
    loadFromLocalStorage()

    return {
        // État
        notifications,
        connected,
        reconnecting,
        unreadCount,
        soundEnabled,
        desktopNotificationsEnabled,

        // Computed
        unreadNotifications,
        recentNotifications,

        // Actions
        connect,
        disconnect,
        addNotification,
        markAsRead,
        markAllAsRead,
        removeNotification,
        clearAll,
        getNotificationTitle,
        getNotificationSeverity,
        toggleSound,
        toggleDesktopNotifications,
        requestDesktopPermission,
        reset
    }
})